import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationCrosshairs, faMap } from '@fortawesome/free-solid-svg-icons';
import { CoffeeShop, getCoffeeIconUrl } from '../hooks/useCoffeeShops';
import { useUserLocation, haversineKm, formatDistance } from '../hooks/useUserLocation';
import { topDrinkOf } from '../utils/shopDerive';

interface NearYouHeroProps {
    coffeeShops: CoffeeShop[];
    coffeeTypesCache: Record<string, { iconName: string; displayName: string }>;
    onOpenMap: () => void;
}

const RADIUS_KM = 5;

const NearYouHero: React.FC<NearYouHeroProps> = ({
    coffeeShops,
    coffeeTypesCache,
    onOpenMap
}) => {
    const navigate = useNavigate();
    const { location, status, error, request } = useUserLocation(false);

    const ranked = useMemo(() => {
        if (!location) return [];
        return coffeeShops
            .filter(s => s.totalRatings > 0 && s.coordinates?.lat && s.coordinates?.lng)
            .map(s => ({
                shop: s,
                distanceKm: haversineKm(location, s.coordinates)
            }))
            .filter(x => x.distanceKm <= RADIUS_KM)
            .sort((a, b) => a.distanceKm - b.distanceKm);
    }, [location, coffeeShops]);

    const top = ranked.slice(0, 3);

    // ---------- locked / locating state ----------
    if (status !== 'granted' || !location) {
        return (
            <div
                className="mb-6 border-brutal border-ink shadow-brutal-sm rounded-sm p-5 sm:p-6"
                style={{ background: '#F2EEE5' }}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-ink/70 mb-1">
                            ◉ NEAR YOU
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-black text-ink leading-tight tracking-tight">
                            Find shops around you.
                        </h2>
                        <p className="text-ink/75 text-sm mt-2 max-w-md">
                            Share your location to surface the closest rated cafés and unlock rate-a-drink.
                        </p>
                        {error && status !== 'requesting' && (
                            <p className="text-xs text-pink-hover font-bold mt-2">{error}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={request}
                        disabled={status === 'requesting'}
                        className="px-4 py-3 bg-ink text-paper border-brutal border-ink shadow-brutal-sm rounded-sm font-black text-xs uppercase tracking-widest hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <FontAwesomeIcon icon={faLocationCrosshairs} />
                        {status === 'requesting' ? 'Locating…' : 'Share location'}
                    </button>
                </div>
            </div>
        );
    }

    // ---------- granted but no nearby rated shops ----------
    if (top.length === 0) {
        return (
            <div className="mb-6 border-brutal border-ink shadow-brutal-sm rounded-sm bg-paper p-5">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-ink/70 mb-1">
                    ◉ NEAR YOU · LIVE
                </div>
                <h2 className="text-xl font-black text-ink leading-tight">No rated shops within {RADIUS_KM} km</h2>
                <p className="text-ink/65 text-sm mt-2">
                    Try the map to explore further out, or be the first to rate a shop near you.
                </p>
                <button
                    type="button"
                    onClick={onOpenMap}
                    className="mt-3 px-3 py-2 bg-ink text-paper border-brutal border-ink shadow-brutal-sm rounded-sm font-black text-xs uppercase tracking-widest inline-flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faMap} /> Open map
                </button>
            </div>
        );
    }

    // ---------- granted with nearby shops ----------
    return (
        <div
            className="mb-6 border-brutal border-ink shadow-brutal-sm rounded-sm overflow-hidden"
            style={{ background: '#F2EEE5' }}
        >
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr]">
                {/* left: title + top 3 list */}
                <div className="p-5 sm:p-6 lg:border-r-brutal lg:border-ink">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-ink/70">
                            ◉ NEAR YOU · LIVE
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-ink/70">
                            {RADIUS_KM} KM · {ranked.length} {ranked.length === 1 ? 'SHOP' : 'SHOPS'}
                        </div>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black text-ink leading-none tracking-tight mb-4">
                        {ranked.length} {ranked.length === 1 ? 'shop' : 'shops'} around you.
                    </h2>

                    <div className="flex flex-col gap-2">
                        {top.map((entry, i) => {
                            const drink = topDrinkOf(entry.shop, coffeeTypesCache);
                            return (
                                <button
                                    key={entry.shop._id}
                                    type="button"
                                    onClick={() => navigate(`/coffeeshops/${entry.shop._id}`)}
                                    className={`grid grid-cols-[18px_28px_1fr_auto] gap-2 sm:gap-3 items-center p-2 rounded-sm border-brutal border-ink shadow-brutal-sm text-left hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-sm transition-all ${
                                        i === 0 ? 'bg-paper' : 'bg-paper/60'
                                    }`}
                                >
                                    <span className="font-black text-sm tabular-nums">
                                        {String.fromCharCode(65 + i)}
                                    </span>
                                    <img
                                        src={getCoffeeIconUrl(drink?.iconName || 'espresso')}
                                        alt=""
                                        className="w-7 h-7 object-contain"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = getCoffeeIconUrl('espresso');
                                        }}
                                    />
                                    <div className="min-w-0">
                                        <div className="font-black text-sm text-ink truncate">
                                            {entry.shop.name}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-ink/60 mt-0.5 truncate">
                                            {formatDistance(entry.distanceKm)}
                                            {drink ? ` · ${drink.displayName}` : ''}
                                        </div>
                                    </div>
                                    <div className="text-base font-black text-ink tabular-nums pr-1">
                                        {entry.shop.averageRating.toFixed(1)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={onOpenMap}
                        className="mt-4 px-3 py-2 bg-ink text-paper border-brutal border-ink shadow-brutal-sm rounded-sm font-black text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-sm transition-all"
                    >
                        Open map →
                    </button>
                </div>

                {/* right: stylized mock map preview */}
                <NearYouMockMap
                    shops={ranked.slice(0, 6)}
                    onOpenMap={onOpenMap}
                />
            </div>
        </div>
    );
};

interface MockMapProps {
    shops: { shop: CoffeeShop; distanceKm: number }[];
    onOpenMap: () => void;
}

/**
 * Lightweight visual map preview — a stylized backdrop with pins arranged
 * by relative bearing/distance from the user. Not meant to be cartographically
 * accurate, just a glance-able hint that there's stuff around.
 */
const NearYouMockMap: React.FC<MockMapProps> = ({ shops, onOpenMap }) => {
    if (shops.length === 0) return null;

    const maxDist = Math.max(...shops.map(s => s.distanceKm), 0.5);
    const center = { x: 50, y: 50 };

    return (
        <button
            type="button"
            onClick={onOpenMap}
            className="relative h-48 lg:h-auto min-h-[200px] cursor-pointer block w-full text-left border-t-brutal lg:border-t-0 border-ink overflow-hidden"
            style={{ background: '#E8DFCE' }}
            aria-label="Open full map"
        >
            {/* grid backdrop */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `
                        repeating-linear-gradient(0deg, transparent 0 40px, rgba(35,35,35,0.06) 40px 41px),
                        repeating-linear-gradient(90deg, transparent 0 40px, rgba(35,35,35,0.06) 40px 41px)
                    `
                }}
            />
            {/* faux roads */}
            <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                <path d="M-20 60% Q 30% 50% 60% 70% T 110% 80%" stroke="#ffffff" strokeWidth="14" fill="none" opacity="0.65" />
                <path d="M40% 0 L 45% 100%" stroke="#ffffff" strokeWidth="10" fill="none" />
                <path d="M0 30% L 100% 35%" stroke="#ffffff" strokeWidth="8" fill="none" />
            </svg>

            {/* shop pins, positioned by polar angle around the center */}
            {shops.map((entry, i) => {
                const radius = 6 + (entry.distanceKm / maxDist) * 32; // % of container
                const angle = (i / shops.length) * 2 * Math.PI - Math.PI / 2;
                const x = center.x + radius * Math.cos(angle);
                const y = center.y + radius * Math.sin(angle);
                const active = i === 0;
                return (
                    <div
                        key={entry.shop._id}
                        className="absolute"
                        style={{
                            left: `${x}%`,
                            top: `${y}%`,
                            transform: 'translate(-50%, -100%)'
                        }}
                    >
                        <div
                            className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full border-brutal border-ink shadow-brutal-sm"
                            style={{ background: active ? '#D9C2A6' : '#FAF7F2' }}
                        >
                            <span className="w-5 h-5 rounded-full bg-ink text-paper grid place-items-center text-[10px] font-black">
                                {String.fromCharCode(65 + i)}
                            </span>
                            <span className="text-xs font-black tabular-nums">
                                {entry.shop.averageRating.toFixed(1)}
                            </span>
                        </div>
                    </div>
                );
            })}

            {/* user position */}
            <div className="absolute" style={{ left: `${center.x}%`, top: `${center.y}%`, transform: 'translate(-50%, -50%)' }}>
                <div className="w-12 h-12 rounded-full bg-pink/30 grid place-items-center">
                    <div className="w-3 h-3 rounded-full bg-pink border-2 border-paper shadow-[0_0_0_1.5px_rgb(35_35_35)]" />
                </div>
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase tracking-widest bg-ink text-paper px-1.5 py-0.5 whitespace-nowrap">
                    YOU
                </div>
            </div>
        </button>
    );
};

export default NearYouHero;
